import { createFileRoute } from "@tanstack/react-router";
import { Check, Phone, ArrowRight, Layers, Droplets, Hammer } from "lucide-react";
import { Reveal } from "../components/reveal";
import { Breadcrumbs } from "../components/breadcrumbs";
import { GuideStrip } from "../components/guide-strip";
import { HomeTrustBadges } from "../components/home-trust-badges";
import { openQuoteModal } from "../components/quote-modal";
import { HARDSCAPE_FACTS } from "../data/hardscape-facts";
import { paverCliftonParkHeroOpt } from "../assets/opt";
import { InlineQuoteSection } from "@/components/inline-quote-section";
import { TopQuoteForm } from "@/components/top-quote-form";

const SITE = "https://capitalpropressurewashing.com";
const URL = `${SITE}/paver-sand-seal`;
const TITLE = "Paver Sand & Seal Albany NY | Polymeric Re-Sanding";
const DESC =
  "Paver sand and seal across Albany & the Capital Region: joint blowout, polymeric sand, vibratory compaction and premium sealer. Stops weeds and shifting pavers.";

const PHONE_DISPLAY = "(518) 900-1913";
const PHONE_TEL = "tel:+15189001913";

const STEPS = [
  { icon: Droplets, title: "1. Deep Clean & Joint Blowout", body: "A paver-safe rotary surface cleaner lifts algae, dirt, and organics off the faces while we flush degraded sand out of every joint down to the base. Sealing over failed sand just locks the failure in." },
  { icon: Layers, title: "2. Polymeric Sand Install", body: "On a dry day we sweep fresh polymeric sand across the hardscape, work it fully into each joint, and blow the paver faces perfectly clean before activation." },
  { icon: Hammer, title: "3. Vibratory Compaction", body: "We compact with a plate so the sand settles instead of bridging, then top off any joints that drop. This is the step budget crews skip — and the reason their sand washes out in one season." },
  { icon: Droplets, title: "4. Activation & Sealer", body: "A controlled mist activates the binders so the sand hardens into a firm, weed-resistant bond. Once cured, we apply your chosen sealer — natural matte, wet-look, or invisible protective." },
];

const SAND_COMPARE = [
  { label: "Weed & moss resistance", poly: "Hardened joints weeds can't root in", plain: "Open seedbed — weeding every weekend" },
  { label: "Washout in heavy rain", poly: "Stays locked in place", plain: "Runs out with every storm" },
  { label: "Paver interlock", poly: "Restored — pavers stop rocking", plain: "Degrades, edges creep" },
  { label: "Ant & insect intrusion", poly: "Blocked", plain: "Ants nest in the joints" },
  { label: "Typical service life", poly: "Years, with periodic top-ups", plain: "One to two seasons" },
];

const FAQS = [
  { q: "What does 'sand and seal' actually mean?", a: "It's the two-part maintenance every paver patio eventually needs: replacing the joint sand that has washed out (sanding), then applying a sealer that protects the pavers and locks the new sand in place (sealing). Cleaning happens first — you can't sand or seal a dirty surface." },
  { q: "Can you seal pavers without re-sanding them?", a: "Only if the existing joint sand is still hard, full to the correct depth, and free of moss or weeds. If it has broken down, sealing over it traps the failure and you'll be redoing the job in a year." },
  { q: "Is polymeric sand better than regular joint sand?", a: "Yes, for almost every Capital Region application. Polymeric sand contains binders that harden when activated with water, so it resists weeds, insects, and washout that plain masonry sand can't handle in our freeze-thaw climate." },
  { q: "How long does the whole job take?", a: "Most residential patios and walkways take two to three visits spread over dry days — cleaning, then sanding and compaction, then sealing. Weather drives the schedule; rushing it is how jobs fail." },
  { q: "How often should pavers be re-sanded and sealed?", a: "Plan on inspecting joints every couple of years. In the Capital Region most hardscape needs sealer refreshed every 3–5 years and joints topped up or replaced as they break down." },
  { q: "Do you offer wet-look sealer?", a: "Yes. We offer natural matte, wet-look color-enhancing, and invisible protective finishes. We'll show you samples on your own pavers before committing." },
];

export const Route = createFileRoute("/paver-sand-seal")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: URL },
      { property: "og:image", content: `${SITE}${paverCliftonParkHeroOpt.url}` },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: `${SITE}${paverCliftonParkHeroOpt.url}` },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          name: "Paver Sand & Seal",
          serviceType: "Paver joint re-sanding and sealing",
          provider: { "@type": "LocalBusiness", name: "Capital Pro Pressure Washing", telephone: "+1-518-900-1913", url: SITE },
          areaServed: HARDSCAPE_FACTS.map((f) => ({ "@type": "City", name: `${f.city}, NY` })),
          url: URL,
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQS.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
        }),
      },
    ],
  }),
  component: PaverSandSealPage,
});

function PaverSandSealPage() {
  return (
    <main>
      <Breadcrumbs items={[{ label: "Paver Cleaning & Sealing", href: "/paver-cleaning-sealing" }, { label: "Paver Sand & Seal" }]} />

      <section className="relative bg-[var(--brand-deep-blue)] text-white overflow-hidden">
        <div aria-hidden className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 20% 20%, #1E6FD9 0, transparent 45%), radial-gradient(circle at 80% 70%, #C8102E 0, transparent 45%)" }} />
        <div className="relative mx-auto max-w-[1200px] px-4 py-16 md:py-24">
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-white/80 mb-3" style={{ fontFamily: "var(--font-display)" }}>
            Capital Region, NY · Polymeric Joint Restoration
          </p>
          <h1 className="text-4xl md:text-6xl uppercase leading-[1.05] tracking-tight max-w-[18ch]" style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>
            Paver Sand &amp; Seal
          </h1>
          <p className="mt-5 max-w-[60ch] text-white/85 text-lg">
            Washed-out joints are why pavers shift, weed, and sink. We blow the joints out, install polymeric sand, compact it in, and seal — the full sand-and-seal process, done in the right order, on dry days.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <button onClick={() => openQuoteModal({ services: ["Paver Cleaning & Sealing"] })} className="inline-flex items-center gap-2 bg-[#C8102E] hover:brightness-90 text-white font-bold px-6 py-3 rounded-md transition">
              Get My Free Quote <ArrowRight size={18} />
            </button>
            <a href={PHONE_TEL} data-call-cta className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 border border-white/25 text-white font-bold px-6 py-3 rounded-md transition">
              <Phone size={18} /> Call {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>
      <TopQuoteForm source="Paver Sand Seal Top" defaultService="pavers" heading="Free Paver Sand & Seal Quote" />

      <HomeTrustBadges />

      <section className="bg-white">
        <div className="mx-auto max-w-[1200px] px-4 py-16">
          <Reveal>
            <h2 className="text-3xl md:text-4xl uppercase text-[var(--brand-deep-blue)]" style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>
              The Sand &amp; Seal Process
            </h2>
            <p className="mt-3 max-w-[70ch] text-[var(--brand-charcoal)]/80">
              Every step exists for a reason. Skip one and the job fails by next spring — usually the compaction step.
            </p>
          </Reveal>
          <div className="mt-10 grid gap-6 md:grid-cols-2">
            {STEPS.map((s) => {
              const Icon = s.icon;
              return (
                <Reveal key={s.title}>
                  <div className="h-full rounded-lg border border-gray-200 p-6 bg-[var(--brand-light-gray)]">
                    <Icon className="text-[#C8102E]" size={26} />
                    <h3 className="mt-3 text-xl uppercase text-[var(--brand-deep-blue)]" style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>{s.title}</h3>
                    <p className="mt-2 text-[var(--brand-charcoal)]/80">{s.body}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="bg-[var(--brand-light-gray)]">
        <div className="mx-auto max-w-[1000px] px-4 py-16">
          <Reveal>
            <h2 className="text-3xl md:text-4xl uppercase text-[var(--brand-deep-blue)]" style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>
              Polymeric Sand vs. Plain Joint Sand
            </h2>
          </Reveal>
          <div className="mt-8 overflow-x-auto">
            <table className="w-full border-collapse bg-white rounded-lg overflow-hidden text-sm">
              <thead>
                <tr className="bg-[var(--brand-deep-blue)] text-white text-left">
                  <th className="p-3">&nbsp;</th>
                  <th className="p-3">Polymeric Sand (what we install)</th>
                  <th className="p-3">Plain Masonry Sand</th>
                </tr>
              </thead>
              <tbody>
                {SAND_COMPARE.map((r) => (
                  <tr key={r.label} className="border-t border-gray-200">
                    <td className="p-3 font-semibold text-[var(--brand-charcoal)]">{r.label}</td>
                    <td className="p-3 text-[var(--brand-charcoal)]/85"><Check className="inline text-green-600 mr-1" size={15} />{r.poly}</td>
                    <td className="p-3 text-[var(--brand-charcoal)]/70">{r.plain}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      <section className="bg-white">
        <div className="mx-auto max-w-[1200px] px-4 py-16">
          <Reveal>
            <h2 className="text-3xl md:text-4xl uppercase text-[var(--brand-deep-blue)]" style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>
              Paver Sand &amp; Seal Near You
            </h2>
            <p className="mt-3 text-[var(--brand-charcoal)]/80">Local crews, local scheduling — pick your town:</p>
          </Reveal>
          <ul className="mt-6 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {HARDSCAPE_FACTS.map((f) => (
              <li key={f.slug}>
                <a href={`/paver-restoration-sealing-${f.slug}-ny`} className="block rounded-md border border-gray-200 px-4 py-3 hover:border-[var(--brand-deep-blue)] hover:bg-[var(--brand-light-gray)] transition text-[var(--brand-bright-blue)] font-semibold">
                  Paver restoration &amp; sealing in {f.city}, NY
                </a>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="bg-[var(--brand-light-gray)]">
        <div className="mx-auto max-w-[900px] px-4 py-16">
          <h2 className="text-3xl md:text-4xl uppercase text-[var(--brand-deep-blue)]" style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>
            Sand &amp; Seal FAQs
          </h2>
          <div className="mt-8 space-y-4">
            {FAQS.map((f) => (
              <details key={f.q} className="rounded-lg bg-white border border-gray-200 p-5">
                <summary className="cursor-pointer font-bold text-[var(--brand-deep-blue)]">{f.q}</summary>
                <p className="mt-3 text-[var(--brand-charcoal)]/80">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[var(--brand-deep-blue)] text-white">
        <div className="mx-auto max-w-[900px] px-4 py-16 text-center">
          <h2 className="text-3xl md:text-4xl uppercase" style={{ fontFamily: "var(--font-display)", fontWeight: 700 }}>
            Get Your Pavers Locked Back Down
          </h2>
          <p className="mt-3 text-white/85">Free, no-pressure quote — usually same week across the Capital Region.</p>
          <div className="mt-7 flex flex-wrap justify-center gap-3">
            <button onClick={() => openQuoteModal({ services: ["Paver Cleaning & Sealing"] })} className="inline-flex items-center gap-2 bg-[#C8102E] hover:brightness-90 text-white font-bold px-6 py-3 rounded-md transition">
              Get My Free Quote <ArrowRight size={18} />
            </button>
            <a href={PHONE_TEL} data-call-cta className="inline-flex items-center gap-2 bg-white text-[var(--brand-deep-blue)] font-bold px-6 py-3 rounded-md transition hover:bg-white/90">
              <Phone size={18} /> Call {PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </section>
    <GuideStrip serviceSlug="paver-cleaning-sealing" serviceName="Paver Cleaning & Sealing" />
      <InlineQuoteSection source="Paver Sand & Seal" defaultService="pavers" />
    </main>
  );
}
